import { useState, Fragment } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AppBar,
  Box,
  Toolbar,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Link,
  Container,
  Avatar,
  Stack,
  useTheme,
  Popover,
  Typography,
  Divider,
} from "@mui/material";
import { LuEqual, LuX, LuArrowRight, LuChevronDown } from "react-icons/lu";
import logo from "../assets/images/logo.png";
import { ReactComponent as NotificationIcon } from "../assets/icons/notification.svg";
import { ReactComponent as NotificationIcon2 } from "../assets/icons/notification2.svg";
import { ReactComponent as LogoutIcon } from "../assets/icons/logout.svg";
import RegisterDialog from "./dialogs/RegisterDialog";
import VerificationDialog from "./dialogs/VerficationDialog";
import HelpCenterDialog from "./dialogs/HelpCenterDialog";
import LogoutDialog from "./dialogs/LogoutDialog";
import { useSelector } from "react-redux";
import NotificationPopover from "./NotificationPopover";
import LanguageSwitcher from "./LanguageSwitcher";

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { t } = useTranslation();

  const { user } = useSelector((state) => state.auth);

  // Check if user is logged in and their role
  const isLoggedIn = !!user;
  const isCreator = user?.role === "creator";
  const isBrand = user?.role === "brand";

  const shouldShowDashboardHeader = isLoggedIn && (isCreator || isBrand);

  // Regular header state
  const [mobileOpen, setMobileOpen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [otpOpen, setOtpOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [logoutOpen, setLogouOpen] = useState(false);

  // Creator header state
  const [anchorEl, setAnchorEl] = useState(null);
  const profilePopoverOpen = Boolean(anchorEl);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const notificationPopoverOpen = Boolean(notificationAnchorEl);

  // Event handlers for creator header
  const handleProfileClick = (event) => setAnchorEl(event.currentTarget);
  const handleProfileClose = () => setAnchorEl(null);
  const handleNotificationClick = (event) =>
    setNotificationAnchorEl(event.currentTarget);
  const handleNotificationClose = () => setNotificationAnchorEl(null);

  // Event handlers for regular header
  const handleRegisterOpen = () => setRegisterOpen(true);
  const handleRegisterClose = () => setRegisterOpen(false);
  const handleOTPClose = () => setOtpOpen(false);
  const handleDrawerToggle = () => setMobileOpen((prevState) => !prevState);
  const handleHelpOpen = () => {
    handleProfileClose();
    setHelpOpen(true);
  };
  const handleHelpClose = () => setHelpOpen(false);
  const handleLogoutOpen = () => {
    handleProfileClose();
    setLogouOpen(true);
  };
  const handleLogoutClose = () => setLogouOpen(false);

  // Regular header menu items with translations
  const menuItems = [
    { text: t("header.menuItems.brand"), href: "" },
    { text: t("header.menuItems.creator"), href: "" },
    { text: t("header.menuItems.pricing"), href: "" },
    { text: t("header.menuItems.aboutUs"), href: "" },
  ];

  const isRegisterTypePage = location.pathname === "/register";
  const isJobDetailsPage =
    location.pathname.startsWith("/creator/job/") ||
    location.pathname.startsWith("/brand/job/");

  // Function to render user initials for avatar fallback
  const getUserInitials = () => {
    if (!user) return "U";

    const firstInitial =
      user.firstName?.charAt(0) ||
      user.lastName?.charAt(0) ||
      user.fullName?.charAt(0) ||
      user.email?.charAt(0) ||
      "U";

    const secondInitial =
      user.lastName?.charAt(0) || user.fullName?.split(" ")[1]?.charAt(0) || "";

    return firstInitial + secondInitial;
  };

  // Regular header drawer content
  const drawer = (
    <Box sx={{ height: "100%" }}>
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Link href="/" onClick={handleDrawerToggle} underline="none">
          <img
            src={logo}
            alt="Logo"
            style={{
              width: 40,
              height: 40,
              objectFit: "contain",
            }}
          />
        </Link>
        <IconButton onClick={handleDrawerToggle} size="small">
          <LuX size={22} color="#000000" />
        </IconButton>
      </Box>

      <List sx={{ flex: 1 }}>
        {menuItems.map((item) => (
          <ListItem
            key={item.text}
            component={Link}
            href={item.href}
            onClick={handleDrawerToggle}
            sx={{
              py: 1.5,
              px: 2,
              transition: "all 0.2s",
              display: "flex",
              alignItems: "center",
              gap: 1,
              textDecoration: "none",
              "&:hover": {
                backgroundColor: "action.hover",
                "& .arrow-icon": {
                  transform: "translateX(4px)",
                },
              },
            }}
          >
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{
                sx: { color: "text.primary" },
              }}
            />
            <LuArrowRight
              className="arrow-icon"
              size={18}
              style={{ transition: "all 0.2s" }}
            />
          </ListItem>
        ))}
      </List>

      <Box sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {user ? (
            <Avatar
              src={user?.profilePicture}
              onClick={() => {
                handleDrawerToggle();
                handleRegisterOpen();
              }}
              sx={{
                cursor: "pointer",
                width: 40,
                height: 40,
                bgcolor: "tertiary.main",
                flexShrink: 0,
                "&:hover": {
                  opacity: 0.8,
                },
              }}
            >
              {!user?.profilePicture && getUserInitials()}
            </Avatar>
          ) : (
            <Avatar
              onClick={() => {
                handleDrawerToggle();
                handleRegisterOpen();
              }}
              sx={{
                cursor: "pointer",
                width: 40,
                height: 40,
                bgcolor: "tertiary.main",
                flexShrink: 0,
                "&:hover": {
                  opacity: 0.8,
                },
              }}
            />
          )}
          <Button
            fullWidth
            color="primary"
            variant="contained"
            onClick={() => {
              handleDrawerToggle();
              navigate("/register");
            }}
          >
            {t("header.actions.register")}
          </Button>
        </Box>
      </Box>
    </Box>
  );

  // Render creator header content
  const renderDashboardHeader = () => (
    <Fragment>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 1, md: 2 },
          mr: "auto",
        }}
      >
        <Typography
          onClick={() => navigate("/")}
          variant="h2"
          fontWeight={800}
          sx={{
            cursor: "pointer",
            color:
              !isRegisterTypePage && !isJobDetailsPage
                ? "primary.main"
                : "common.white",
          }}
        >
          C
          <Box
            component="span"
            sx={{
              color: isJobDetailsPage
                ? "secondary.main"
                : !isRegisterTypePage
                ? "common.black"
                : "common.white",
            }}
          >
            B
          </Box>
        </Typography>
        {location.pathname !== `/${user?.role}/dashboard` && (
          <Link
            href={`/${user?.role}/dashboard`}
            underline="none"
            sx={{
              display: "flex",
              color: isJobDetailsPage ? "white" : "primary.main",
            }}
          >
            {t("header.actions.backToDashboard")}
          </Link>
        )}
      </Box>

      <Stack direction="row" spacing={{ xs: 1, md: 2 }} alignItems="center">
        {/* Notification Icon */}
        {isLoggedIn && (
          <IconButton
            onClick={handleNotificationClick}
            sx={{
              p: 1,
              backgroundColor: isJobDetailsPage
                ? "white"
                : theme.palette.tertiary.main,
              width: "auto",
              height: "auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              borderRadius: "32px",
              "&:hover": {
                backgroundColor: isJobDetailsPage
                  ? "white"
                  : theme.palette.tertiary.light,
              },
            }}
          >
            {isJobDetailsPage ? (
              <NotificationIcon2 height="28px" width="28px" />
            ) : (
              <NotificationIcon height="24px" width="24px" />
            )}
          </IconButton>
        )}

        {/* Profile Section */}
        <Stack
          direction="row"
          gap={1}
          justifyContent="center"
          alignItems="center"
          sx={{
            borderRadius: "32px",
            backgroundColor: isJobDetailsPage
              ? "white"
              : theme.palette.tertiary.main,
            p: 1,
            cursor: "pointer",
            "&:hover": {
              backgroundColor: isJobDetailsPage
                ? "white"
                : theme.palette.tertiary.light,
            },
          }}
          onClick={handleProfileClick}
        >
          <Avatar
            src={user?.profilePicture}
            sx={{
              bgcolor: isJobDetailsPage ? "tertiary.main" : "secondary.main",
              width: 32,
              height: 32,
              fontSize: 14,
              fontWeight: 500,
              color: "black",
            }}
          >
            {!user?.profilePicture && getUserInitials()}
          </Avatar>
          <LuChevronDown
            size={22}
            color={
              isJobDetailsPage
                ? theme.palette.common.black
                : theme.palette.common.white
            }
          />
        </Stack>

        {/* LanguageSwitcher */}
        <LanguageSwitcher />
      </Stack>

      {/* Profile Popover */}
      <Popover
        open={profilePopoverOpen}
        anchorEl={anchorEl}
        onClose={handleProfileClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            mt: 1.5,
            width: 300,
            p: 2,
            boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.15)",
            borderRadius: 2,
          },
        }}
      >
        {/* Profile Info */}
        <Box>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar
              src={user?.profilePicture}
              sx={{
                bgcolor: "secondary.main",
                width: 50,
                height: 50,
                color: "black",
              }}
            >
              {!user?.profilePicture && getUserInitials()}
            </Avatar>
            <Box>
              <Typography
                variant="body1"
                fontWeight={600}
                sx={{
                  "&:hover": {
                    fontWeight: 700,
                  },
                }}
              >
                {user?.firstName && user?.lastName
                  ? `${user.firstName} ${user.lastName}`
                  : user?.fullName || user?.email.split("@")[0]}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  "&:hover": {
                    fontWeight: 500,
                  },
                }}
              >
                {user?.email}
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="contained"
            fullWidth
            sx={{ mt: 2, py: 1.2 }}
            onClick={() => {
              if (isCreator) {
                navigate("/creator/complete-profile");
              } else {
                navigate("/brand/edit-profile");
              }
              handleProfileClose();
            }}
          >
            {t("header.actions.editProfile")}
          </Button>
        </Box>

        {/* Action Section - Can be conditional based on role */}
        <Box sx={{ mt: 3, cursor: "pointer" }}>
          {isCreator ? (
            <>
              <Typography
                variant="body1"
                fontWeight={500}
                onClick={() => {
                  navigate("/creator/dashboard?tab=applied");
                  handleProfileClose();
                }}
              >
                {t("header.creatorActions.appliedJobs")}
              </Typography>
              <Typography
                variant="body1"
                fontWeight={500}
                mt={1}
                onClick={() => {
                  navigate("/creator/dashboard?tab=inProgress");
                  handleProfileClose();
                }}
              >
                {t("header.creatorActions.inProgress")}
              </Typography>
              <Typography
                mt={1}
                variant="body1"
                fontWeight={500}
                onClick={() => {
                  // navigate("/creator/my-earnings");
                  handleProfileClose();
                }}
              >
                {t("header.creatorActions.myEarnings")}
              </Typography>
              <Typography
                mt={1}
                variant="body1"
                fontWeight={500}
                onClick={() => {
                  // navigate("/creator/my-subscription");
                  handleProfileClose();
                }}
              >
                {t("header.creatorActions.mySubscription")}
              </Typography>
            </>
          ) : (
            <>
              <Typography
                variant="body1"
                fontWeight={500}
                onClick={() => {
                  navigate("/brand/dashboard?tab=active");
                  handleProfileClose();
                }}
              >
                {t("header.brandActions.activeJobs")}
              </Typography>
              <Typography
                variant="body1"
                fontWeight={500}
                mt={1}
                onClick={() => {
                  navigate("/brand/my-product");
                  handleProfileClose();
                }}
              >
                {t("header.brandActions.myProduct")}
              </Typography>
              <Typography
                mt={1}
                variant="body1"
                fontWeight={500}
                onClick={() => {
                  navigate("/brand/dashboard?tab=creatorspotlight");
                  handleProfileClose();
                }}
              >
                {t("header.brandActions.creatorSpotlight")}
              </Typography>
              <Typography
                mt={1}
                variant="body1"
                fontWeight={500}
                onClick={() => {
                  // navigate("/brand/transaction-history");
                  handleProfileClose();
                }}
              >
                {t("header.brandActions.transactionHistory")}
              </Typography>
            </>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Help & Logout */}
        <Box sx={{ cursor: "pointer" }}>
          <Typography variant="body1" fontWeight={500} onClick={handleHelpOpen}>
            {t("header.actions.helpCenter")}
          </Typography>
          <Box
            onClick={handleLogoutOpen}
            sx={{
              display: "flex",
              alignItems: "center",
              mt: 1,
              borderRadius: 2,
              py: 1.2,
              justifyContent: "flex-start",
              textAlign: "left",
              fontWeight: 500,
            }}
          >
            <LogoutIcon />
            <Typography
              sx={{
                ml: 1,
                color: theme.palette.error.main,
              }}
            >
              {t("header.actions.logout")}
            </Typography>
          </Box>
        </Box>
      </Popover>

      {/* Notification Popover */}
      <NotificationPopover
        open={notificationPopoverOpen}
        anchorEl={notificationAnchorEl}
        onClose={handleNotificationClose}
      />
    </Fragment>
  );

  // Render regular header content
  const renderRegularHeader = () => (
    <Fragment>
      <Link href="/" underline="none" sx={{ display: "flex", mr: "auto" }}>
        <Typography
          variant="h2"
          fontWeight={800}
          sx={{
            color: !isRegisterTypePage ? "primary.main" : "common.white",
          }}
        >
          C
          <Box
            component="span"
            sx={{
              color: !isRegisterTypePage ? "common.black" : "common.white",
            }}
          >
            B
          </Box>
        </Typography>
      </Link>

      <Stack direction="row" spacing={{ xs: 1, md: 1.5 }} alignItems="center">
        <Box
          sx={{
            display: isRegisterTypePage ? "flex" : { xs: "none", md: "flex" },
            alignItems: "center",
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: !isRegisterTypePage ? "flex" : "none",
              alignItems: "center",
              gap: 1,
            }}
          >
            {menuItems.map((item) => (
              <Link key={item.text} href={item.href} underline="none">
                <Button
                  sx={{
                    color: "text.primary",
                    textTransform: "none",
                    fontWeight: 500,
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  {item.text}
                </Button>
              </Link>
            ))}
          </Box>

          {user ? (
            <Avatar
              src={user?.profilePicture}
              onClick={handleRegisterOpen}
              sx={{
                cursor: "pointer",
                ml: 1,
                bgcolor: "tertiary.main",
                "&:hover": {
                  opacity: 0.8,
                },
              }}
            >
              {!user?.profilePicture && getUserInitials()}
            </Avatar>
          ) : (
            <Avatar
              onClick={handleRegisterOpen}
              sx={{
                cursor: "pointer",
                ml: 1,
                bgcolor: "tertiary.main",
                "&:hover": {
                  opacity: 0.8,
                },
              }}
            />
          )}

          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate("/register")}
            sx={{
              ml: 1,
            }}
          >
            {t("header.actions.register")}
          </Button>
        </Box>

        <LanguageSwitcher />

        {!isRegisterTypePage && (
          <IconButton
            onClick={handleDrawerToggle}
            sx={{
              display: { md: "none" },
              ml: 1,
            }}
          >
            <LuEqual color="#000000" />
          </IconButton>
        )}
      </Stack>
    </Fragment>
  );

  return (
    <Fragment>
      <AppBar
        position="absolute"
        color="default"
        elevation={0}
        sx={{
          zIndex: (theme) => theme.zIndex.appBar,
          width: "100%",
          top: 0,
          left: 0,
          bgcolor: "transparent",
          mb: 0.5,
        }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4, lg: 6 } }}>
          <Toolbar
            sx={{
              py: shouldShowDashboardHeader ? 1.5 : 2,
              px: "0px !important",
              minHeight: shouldShowDashboardHeader
                ? "auto"
                : { xs: 64, sm: 70 },
            }}
          >
            {shouldShowDashboardHeader
              ? renderDashboardHeader()
              : renderRegularHeader()}
          </Toolbar>
        </Container>

        {/* Only render drawer for regular header */}
        {!shouldShowDashboardHeader && (
          <Drawer
            anchor="right"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: "block", md: "none" },
              "& .MuiDrawer-paper": {
                width: "100%",
                maxWidth: 240,
                boxSizing: "border-box",
              },
            }}
          >
            {drawer}
          </Drawer>
        )}
      </AppBar>

      {/* Only render dialogs for regular header */}
      {!shouldShowDashboardHeader && (
        <>
          <RegisterDialog
            open={registerOpen}
            onClose={handleRegisterClose}
            setOtpOpen={setOtpOpen}
          />
          <VerificationDialog
            open={otpOpen}
            onClose={handleOTPClose}
            setRegisterOpen={setRegisterOpen}
          />
        </>
      )}

      <HelpCenterDialog open={helpOpen} onClose={handleHelpClose} />
      <LogoutDialog open={logoutOpen} onClose={handleLogoutClose} />
    </Fragment>
  );
};

export default Header;
